import { useStore } from '@nanostores/react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { GithubConnectedDialog } from '~/components/github/GithubConnectedDialog';
import { GithubCreateRepoDialog } from '~/components/github/GithubCreateRepoDialog';
import { clientFetch } from '~/lib/.client/fetch';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { useGithubToken } from '~/lib/client-github';
import { DEFAULT_GITHUB_BRANCH } from '~/lib/constants';
import { useGithubStore } from '~/lib/context/github';
import { createRepository, GraphQLError } from '~/lib/github-api';
import { forkProject } from '~/lib/projects/forkProject';
import { projectStore } from '~/lib/stores/project';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import type { RepoFullName } from '~/types/github';
import { ClientError } from '~/utils/client-error';
import { invariant } from '~/utils/invariant';
import { logger } from '~/utils/logger';

export interface RepoNamespace extends RepoFullName {
  id: string;
  avatarUrl?: string;
}

export function useGithubCreateRepoFlow() {
  const [open, setOpen] = useState(false);

  const dialog = open && <GithubCreateRepoFlow open onClose={() => setOpen(false)} />;

  return {
    dialog,
    open() {
      setOpen(true);
    },
  };
}

function GithubCreateRepoFlow({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [chosenRepo, setChosenRepo] = useState<RepoNamespace>();

  const user = useStore(userStore.user);

  const github = useGithubStore();
  const getGithubToken = useGithubToken();

  const username = useStore(github.username);

  if (import.meta.env.DEV) {
    invariant(user);
  }

  async function chooseRepo(repo: RepoNamespace) {
    if (!username) {
      return;
    }

    try {
      await tryChooseRepo(repo);
    } catch (error: any) {
      logger.error(error);

      const message = `Unable to create ${repo.owner}/${repo.name}`;

      if (error instanceof ClientError) {
        toast.error(`${message}: ${error.message}`);
      } else {
        toast.error(message);
      }

      return;
    }

    setChosenRepo(repo);

    toast.success(`Successfully created repository ${repo.owner}/${repo.name}`);
  }

  async function tryChooseRepo(repo: RepoNamespace) {
    if (projectStore.shouldFork()) {
      try {
        await forkProject();
      } catch (error) {
        throw new ClientError('Forking project failed', { cause: error });
      }
    }

    const branchName = DEFAULT_GITHUB_BRANCH;

    const token = await getGithubToken();

    if (!token) {
      throw new ClientError('No GitHub credentials');
    }

    let repoId: string;

    const orgRepo = repo.owner === username ? undefined : repo;

    try {
      const repository = await createRepository({ name: repo.name, ownerId: orgRepo?.id }, token);

      repoId = repository.id;
    } catch (error) {
      if (error instanceof GraphQLError) {
        if (error.contains('FORBIDDEN')) {
          throw new ClientError('Insufficient credentials', { cause: error });
        }
      }

      throw error;
    }

    const projectId = projectStore.getProjectId();

    invariant(projectId);

    const response = await clientFetch(`/api/project/integrations/github/${projectId}`, {
      method: 'POST',
      body: JSON.stringify({ repository: { owner: repo.owner, name: repo.name, id: repoId }, branchName }),
    });

    if (!response.ok) {
      throw new Error('Error updating integration');
    }

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.GithubRepoCreated,
        properties: {
          repositoryName: repo.name,
          repositoryOwner: repo.owner,
          repositoryId: orgRepo?.id,
        },
      },
    });

    github.connectToRepo({ branchName, repository: repo });
  }

  return (
    open && (
      <>
        {!chosenRepo && username && (
          <GithubCreateRepoDialog username={username} user={user} onChoose={chooseRepo} open onClose={onClose} />
        )}
        {chosenRepo && username && <GithubConnectedDialog onClose={onClose} open repo={chosenRepo} />}
      </>
    )
  );
}
